import type { IGlobalLimits, IPlanFeatures, IPlanResponse } from '@escloud/types';
import { Check } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * At or above this, a quota is presented as unlimited rather than as a number.
 * A client-side display convention only: the server defines no such sentinel,
 * it seeds implausibly large numbers for uncapped plans, and enforcement
 * compares against the real value either way. Copied from the legacy plan
 * card (`pages/tenants/[tenantId]/select-plan.tsx`), removed with the legacy
 * funnel (ESCLOUD-334).
 */
const UNLIMITED_AT = 1_000_000;

/**
 * The quota lines of a plan card. Total over `IGlobalLimits`, so a limit added
 * to the contract is a compile error here instead of a quota the customer
 * never sees; rendered in the order written below.
 */
const LIMIT_LINES: Record<keyof IGlobalLimits, (value: number) => string> = {
    cases_per_month: (value) => `${formatLimit(value)} cases / month`,
    jobs_per_month: (value) => `${formatLimit(value)} jobs / month`,
    storage_gb: (value) => `${formatStorage(value)} storage`,
    members: (value) => `${formatLimit(value)} team members`,
};

/**
 * How each feature flag is advertised. Total over `IPlanFeatures` for the same
 * reason as the limits, and `null` is a deliberate "not advertised yet" – the
 * only way a flag stays off the card, so it has to be written down rather than
 * forgotten by omission.
 */
const FEATURE_LABELS: Record<keyof Required<IPlanFeatures>, string | null> = {
    priority_processing: 'Priority processing',
    api_access: 'API access',
    custom_branding: 'Custom branding',
    // Enterprise SSO is not sold yet and is false on every plan – a struck-through
    // line on every card would advertise a product (ESCLOUD-274).
    sso: null,
};

const LIMIT_KEYS = Object.keys(LIMIT_LINES) as Array<keyof IGlobalLimits>;
const FEATURE_KEYS = Object.keys(FEATURE_LABELS) as Array<keyof Required<IPlanFeatures>>;

/**
 * What the card's button says and whether it may be pressed. `price: null`
 * (or a null `unitAmount`) means the provider could not price the plan: when
 * Stripe is wired that is an outage and the plan is unavailable, never free.
 * When it is not wired (mock billing) the server activates directly, so the
 * plan stays choosable and the price is "shown at checkout".
 */
export function planCta(plan: IPlanResponse, paymentsWired: boolean): { label: string; enabled: boolean } {
    if (plan.price === null || plan.price.unitAmount === null) {
        return paymentsWired
            ? { label: 'Price unavailable', enabled: false }
            : { label: `Choose ${plan.name}`, enabled: true };
    }
    if (plan.price.unitAmount === 0) return { label: 'Start free', enabled: true };
    return { label: `Choose ${plan.name}`, enabled: true };
}

/**
 * A single plan on the public pricing page (ESCLOUD-332). Limit and feature
 * tables are copied from the legacy onboarding plan card rather than shared
 * with it, because that page is removed with the legacy funnel (ESCLOUD-334).
 */
export function PlanCard({
    plan,
    onChoose,
    disabled,
    paymentsWired = true,
}: {
    plan: IPlanResponse;
    onChoose: () => void;
    disabled?: boolean;
    paymentsWired?: boolean;
}) {
    const cta = planCta(plan, paymentsWired);
    const { limits, features } = plan;

    return (
        <div
            className={cn(
                'relative flex h-full flex-col gap-3.5 rounded-xl border bg-card p-5 pb-6',
                plan.highlight ? 'border-primary ring-1 ring-primary' : 'border-border'
            )}
            data-testid={`plan-card-${plan.id}`}
        >
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-[16.5px] font-medium tracking-[-0.012em] text-foreground">
                        {plan.name}
                    </div>
                    {plan.description && (
                        <div className="mt-0.5 text-[13px] text-muted-foreground">{plan.description}</div>
                    )}
                </div>
                {plan.highlight && (
                    <Badge className="font-mono text-[9.5px] tracking-[0.06em] uppercase">Most chosen</Badge>
                )}
            </div>

            {plan.price && plan.price.unitAmount !== null ? (
                <div className="flex items-baseline gap-1.5">
                    <span className="text-[28px] leading-none font-medium tracking-[-0.03em] text-foreground">
                        {formatPrice(plan.price.unitAmount, plan.price.currency)}
                    </span>
                    {plan.price.unitAmount > 0 && (
                        <span className="text-sm text-muted-foreground">
                            / {plan.price.interval ?? 'month'}
                        </span>
                    )}
                </div>
            ) : (
                // No resolvable price (billing not wired here, or the provider
                // was unreachable): unavailable, never free – Checkout is the
                // authority on what is actually charged.
                <div className="flex items-baseline gap-1.5">
                    <span className="text-[28px] leading-none font-medium text-muted-foreground/50">–</span>
                    <span className="text-sm text-muted-foreground">
                        {paymentsWired ? 'Price unavailable' : 'Price shown at checkout'}
                    </span>
                </div>
            )}

            <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {LIMIT_KEYS.map((key) => (
                    <PlanLine key={key} label={LIMIT_LINES[key](limits.global[key])} />
                ))}
                {FEATURE_KEYS.map((key) => {
                    const label = FEATURE_LABELS[key];
                    return label === null ? null : (
                        <PlanLine key={key} label={label} muted={!features[key]} />
                    );
                })}
            </ul>

            <div className="flex-1" />
            <Button
                type="button"
                variant={plan.highlight ? 'default' : 'outline'}
                onClick={onChoose}
                disabled={disabled || !cta.enabled}
            >
                {cta.label}
            </Button>
        </div>
    );
}

function PlanLine({ label, muted }: { label: string; muted?: boolean }) {
    return (
        <li
            className={cn(
                'flex items-start gap-2 text-[13px] leading-snug',
                muted ? 'text-muted-foreground/50 line-through' : 'text-muted-foreground'
            )}
        >
            <Check
                className={cn('mt-0.5 h-3 w-3 shrink-0', muted ? 'text-muted-foreground/30' : 'text-primary')}
                strokeWidth={2.5}
            />
            <span>{label}</span>
        </li>
    );
}

/**
 * Format a provider amount (smallest currency unit) in the price's own
 * currency – whole amounts without decimals ("€99"), fractional ones with
 * ("€99.50"). The currency comes from Stripe, lowercase ISO 4217. Copied
 * from the legacy plan card.
 */
function formatPrice(unitAmount: number, currency: string): string {
    const amount = unitAmount / 100;
    return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency.toUpperCase(),
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
}

function formatLimit(value: number): string {
    if (value >= UNLIMITED_AT) return 'Unlimited';
    return value.toLocaleString();
}

function formatStorage(gb: number): string {
    if (gb >= UNLIMITED_AT) return 'Unlimited';
    if (gb >= 1000) return `${(gb / 1000).toFixed(0)} TB`;
    return `${gb} GB`;
}
